/**
 * HighlightNote — a callout with a HAIRLINE CONTOUR, never a filled wash.
 *
 * The canonical replacement for the "``bg-ando-orange-soft`` as a section
 * background" anti-pattern (see the ando-ui-review skill). Brand emphasis is
 * a thin orange border on paper (``tone="accent"``), not an orange block.
 * Use for "why now" signals, tips, AI-analysis callouts, inline hints.
 *
 * ``tone``:
 *   - ``accent``  → orange hairline + orange eyebrow (the brand highlight).
 *   - ``neutral`` → plain hairline (a quiet framed note, e.g. rationale text).
 */
import type * as React from "react";

import { cn } from "../../lib/cn";

export type HighlightTone = "accent" | "neutral";

export interface HighlightNoteProps {
  /** Optional uppercase eyebrow (e.g. "Perché ora"). */
  readonly label?: React.ReactNode;
  /** Optional leading glyph next to the eyebrow (accent tone tints it orange). */
  readonly icon?: React.ReactNode;
  readonly tone?: HighlightTone;
  readonly children: React.ReactNode;
  readonly className?: string;
}

export const HighlightNote: React.FC<HighlightNoteProps> = ({
  label,
  icon,
  tone = "accent",
  children,
  className,
}) => {
  const accent = tone === "accent";
  return (
    <div
      className={cn(
        "rounded-xl border bg-transparent p-3.5",
        accent ? "border-ando-orange/30" : "border-hairline",
        className,
      )}
    >
      {label ? (
        <span
          className={cn(
            "flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em]",
            accent ? "text-ando-orange" : "text-ink-3",
          )}
        >
          {icon ? (
            <span className="shrink-0" aria-hidden>
              {icon}
            </span>
          ) : null}
          {label}
        </span>
      ) : null}
      <div className={cn("text-[12.5px] leading-relaxed text-ink-2", label && "mt-1.5")}>
        {children}
      </div>
    </div>
  );
};

HighlightNote.displayName = "HighlightNote";
